/**
 * Anonymization utility for modality data
 * Replaces real site names, addresses, and coordinates with fake but realistic data
 */

// Deterministic hash function to ensure same input always produces same output
const hashString = (str) => {
  let hash = 0
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i)
    hash = ((hash << 5) - hash) + char
    hash = hash & hash // Convert to 32bit integer
  }
  return Math.abs(hash)
}

// Fake facility name prefixes and suffixes
const FACILITY_PREFIXES = [
  'Regional', 'Central', 'North', 'South', 'East', 'West', 'Metro', 'Community',
  'Memorial', 'General', 'Medical', 'Health', 'Care', 'Clinic', 'Hospital', 'Center'
]

const FACILITY_SUFFIXES = [
  'Medical Center', 'Health Center', 'Hospital', 'Clinic', 'Care Center',
  'Medical Facility', 'Imaging Center', 'Diagnostic Center', 'Health Facility'
]

// Generate a fake facility name based on a hash of the real name
const generateFakeSiteName = (realSiteName) => {
  if (!realSiteName) return 'Demo Facility'
  
  const hash = hashString(realSiteName)
  const prefixIndex = hash % FACILITY_PREFIXES.length
  const suffixIndex = (hash >> 8) % FACILITY_SUFFIXES.length
  const number = (hash >> 16) % 100
  
  const prefix = FACILITY_PREFIXES[prefixIndex]
  const suffix = FACILITY_SUFFIXES[suffixIndex]
  
  return number > 0 ? `${prefix} ${number} ${suffix}` : `${prefix} ${suffix}`
}

// Generate fake but realistic coordinates (clustered in a demo region)
// Using a generic US region (e.g., central US) for demo purposes
const DEMO_BASE_LAT = 39.8283  // Central US latitude
const DEMO_BASE_LON = -98.5795 // Central US longitude
const DEMO_RANGE = 5.0 // ~5 degree range for clustering

const generateFakeCoordinates = (realSiteName) => {
  if (!realSiteName) {
    return {
      latitude: DEMO_BASE_LAT + (Math.random() - 0.5) * DEMO_RANGE,
      longitude: DEMO_BASE_LON + (Math.random() - 0.5) * DEMO_RANGE
    }
  }
  
  const hash = hashString(realSiteName)
  // Use hash to generate deterministic but varied coordinates
  const latOffset = ((hash % 1000) / 1000 - 0.5) * DEMO_RANGE
  const lonOffset = (((hash >> 10) % 1000) / 1000 - 0.5) * DEMO_RANGE
  
  return {
    latitude: DEMO_BASE_LAT + latOffset,
    longitude: DEMO_BASE_LON + lonOffset
  }
}

// Generate fake address
const generateFakeAddress = (realSiteName) => {
  if (!realSiteName) return '123 Demo Street, Demo City, ST 12345'
  
  const hash = hashString(realSiteName)
  const streetNumber = 100 + (hash % 900)
  const streetNames = ['Main', 'Oak', 'Park', 'Maple', 'Elm', 'Cedar', 'Pine', 'First', 'Second', 'Third']
  const streetName = streetNames[hash % streetNames.length]
  const cities = ['Demo City', 'Sample Town', 'Exampleville', 'Test City', 'Demo Town']
  const city = cities[(hash >> 8) % cities.length]
  
  return `${streetNumber} ${streetName} Street, ${city}, ST ${10000 + (hash % 90000)}`
}

// Generate fake serial number (optional)
const generateFakeSerialNumber = (realSerialNumber) => {
  if (!realSerialNumber) return 'DEMO-XXXXX'
  
  const hash = hashString(realSerialNumber)
  return `DEMO-${String(hash).slice(0, 8).padStart(8, '0')}`
}

/**
 * Anonymize a single modality object
 * @param {Object} modality - The modality object to anonymize
 * @returns {Object} - Anonymized modality object
 */
export const anonymizeModality = (modality) => {
  if (!modality) return modality
  
  const fakeSiteName = generateFakeSiteName(modality.siteName)
  const fakeCoords = generateFakeCoordinates(modality.siteName || modality.id?.toString())
  
  return {
    ...modality,
    siteName: fakeSiteName,
    siteAddress: generateFakeAddress(modality.siteName || modality.id?.toString()),
    latitude: fakeCoords.latitude,
    longitude: fakeCoords.longitude,
    // Update displayName if it exists (usually contains siteName)
    displayName: modality.displayName 
      ? modality.displayName.replace(modality.siteName || '', fakeSiteName)
      : `${fakeSiteName} ${modality.model || ''} ${modality.serialNumber || ''}`.trim(),
    // Optionally anonymize serial number (uncomment if needed)
    // serialNumber: generateFakeSerialNumber(modality.serialNumber)
  }
}

/**
 * Anonymize an array of modalities
 * @param {Array} modalities - Array of modality objects
 * @returns {Array} - Array of anonymized modality objects
 */
export const anonymizeModalities = (modalities) => {
  if (!Array.isArray(modalities)) return modalities
  return modalities.map(anonymizeModality)
}


